import React, { useEffect, useState } from 'react';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { IconButton, Typography, useMediaQuery } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Utils from '../../../utils';

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // table-layout fixed + <col> en %: la tabla ocupa siempre el 100% del ancho
    table: {
      width: '100%',
      tableLayout: 'fixed',
      '& td, & th': {
        padding: theme.spacing(1.5, 1),
        wordBreak: 'break-word',
      },
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
    cardTitle: {
      fontWeight: 600,
      wordBreak: 'break-word',
    },
    cardDetail: {
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.5),
    },
    cardFields: {
      margin: theme.spacing(1, 0, 0),
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      columnGap: theme.spacing(2),
      rowGap: theme.spacing(0.5),
      '& dt': { color: theme.palette.text.secondary },
      '& dd': { margin: 0, textAlign: 'right' },
    },
    cardActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  }),
);

const texts = {
  table: {
    headers: {
      code: 'Código',
      description: 'Descripción',
      price: 'Precio',
      finalPrice: 'Precio final',
      finalTotal: 'Precio total',
      quantity: 'Cantidad',
      delete: 'Eliminar'
    }
  }
}

// Porcentajes de ancho de cada columna (suman 100). En tablet vertical se oculta "Precio"
// y los demás se redistribuyen.
const COLUMNS = [
  { key: 'code', pct: 12 },
  { key: 'description', pct: 28 },
  { key: 'price', pct: 14, hideOnTablet: true },
  { key: 'finalPrice', pct: 14 },
  { key: 'finalTotal', pct: 14 },
  { key: 'quantity', pct: 9 },
  { key: 'delete', pct: 9 },
]

export default function ItemTable({ rows, handleTable, disabledForm }: { rows: Array<any>, handleTable: Function, disabledForm: boolean }) {

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'sm'));
  const [reload, setReload] = useState(false);

  useEffect(() => {
    setReload(false)
  }, [reload])

  const deleteButton = (row: any) => (
    <IconButton
      aria-label="Eliminar producto"
      onClick={() => handleTable('REMOVE', row)}
      disabled={disabledForm}
    >
      <DeleteIcon />
    </IconButton>
  )

  // ---- mobile: una card por producto ----
  if (isMobile) {
    if (rows.length === 0) {
      return <Typography color="textSecondary">Aún no has agregado productos.</Typography>
    }
    return (
      <div>
        {rows.map((row, index) => (
          <Paper key={index} variant="outlined" className={classes.card}>
            <Typography className={classes.cardTitle}>{row.description}</Typography>
            {row.detailedDescription ? (
              <div className={classes.cardDetail}>{row.detailedDescription}</div>
            ) : null}
            <dl className={classes.cardFields}>
              <dt>{texts.table.headers.code}</dt>
              <dd>{row.code}</dd>
              <dt>{texts.table.headers.price}</dt>
              <dd>{Utils.formatNumber(row.price)}</dd>
              <dt>{texts.table.headers.finalPrice}</dt>
              <dd>{Utils.formatNumber(Utils.getItemFinalUnitPrice(row))}</dd>
              <dt>{texts.table.headers.quantity}</dt>
              <dd>{row.quantity}</dd>
              <dt>{texts.table.headers.finalTotal}</dt>
              <dd><b>{Utils.formatNumber(Utils.getItemFinalLineTotal(row))}</b></dd>
            </dl>
            <div className={classes.cardActions}>{deleteButton(row)}</div>
          </Paper>
        ))}
      </div>
    )
  }

  // ---- tablet / desktop: tabla con columnas en % ----
  const visible = COLUMNS.filter(c => !(isTablet && c.hideOnTablet))
  const total = visible.reduce((sum, c) => sum + c.pct, 0)
  const show = (key: string) => visible.some(c => c.key === key)

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table} aria-label="Productos a facturar">
        <colgroup>
          {visible.map(c => <col key={c.key} style={{ width: `${(c.pct / total) * 100}%` }} />)}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell>{texts.table.headers.code}</TableCell>
            <TableCell>{texts.table.headers.description}</TableCell>
            {show('price') && <TableCell align="right">{texts.table.headers.price}</TableCell>}
            <TableCell align="right">{texts.table.headers.finalPrice}</TableCell>
            <TableCell align="right">{texts.table.headers.finalTotal}</TableCell>
            <TableCell align="right">{texts.table.headers.quantity}</TableCell>
            <TableCell align="right">{texts.table.headers.delete}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell component="th" >{row.code}</TableCell>
                <TableCell component="th">
                  {row.description}
                  {row.detailedDescription ? (
                    <div style={{ fontSize: '0.75rem', marginTop: 4 }}>{row.detailedDescription}</div>
                  ) : null}
                </TableCell>
                {show('price') && <TableCell align="right">{Utils.formatNumber(row.price)}</TableCell>}
                <TableCell align="right">{Utils.formatNumber(Utils.getItemFinalUnitPrice(row))}</TableCell>
                <TableCell align="right">{Utils.formatNumber(Utils.getItemFinalLineTotal(row))}</TableCell>
                <TableCell align="right">{row.quantity}</TableCell>
                <TableCell align="right">{deleteButton(row)}</TableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
}
